import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { ChevronDown, X } from 'lucide-react';

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  /** Stable DOM id of the toggle button. */
  id: string;
  /** The dimension name («Статус») — the visible name of the closed control. */
  label: string;
  /** Closed-button text when nothing is selected («Все статусы»). */
  placeholder: string;
  options: readonly MultiSelectOption[];
  selected: readonly string[];
  /** Sends the WHOLE new list (a checkbox toggle is one value in/out). */
  onChange: (values: string[]) => void;
  testId?: string;
}

/**
 * Multi-value filter control (owner: several statuses / streams / people /
 * teams / priorities at once): a closed button — «Статус · 2», or the
 * placeholder when the selection is empty — opening a checkbox-list popover.
 *
 * The popover is `position: fixed` and placed from the toggle's client rect,
 * exactly like the DatePicker's calendar: an absolutely positioned child would
 * be clipped by `.table-wrap`'s `overflow-x: auto`. It closes on an outside
 * press, on Escape (captured before any dialog handler, focus returns to the
 * toggle) and on scroll — a fixed popover must never drift from its button.
 * The scroll listener is CAPTURE-phase, so it also sees a scroll that starts
 * inside the popover (the options list); there the popover is the scroll
 * container itself and cannot drift — it stays open (otherwise a long
 * dictionary could never be scrolled at all).
 */
export function MultiSelect({ id, label, placeholder, options, selected, onChange, testId }: MultiSelectProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    setPos(null);
    if (refocus) buttonRef.current?.focus();
  }, []);

  // Place the fixed popover next to the toggle; flip above when the space
  // below runs out (the toolbar sits at the top, so this is mostly for 900px
  // viewports with a long dictionary list).
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const button = buttonRef.current;
      const popover = popoverRef.current;
      if (!button || !popover) return;
      const rect = button.getBoundingClientRect();
      const width = popover.offsetWidth;
      const height = popover.offsetHeight;
      const margin = 8;
      const gap = 6;
      const left = Math.max(margin, Math.min(rect.left, window.innerWidth - width - margin));
      const below = rect.bottom + gap;
      const fitsBelow = below + height <= window.innerHeight - margin;
      const fitsAbove = rect.top - gap - height >= margin;
      setPos({ top: fitsBelow || !fitsAbove ? below : rect.top - gap - height, left });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      close(true);
    };
    const onPointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) close(false);
    };
    const onScroll = (event: Event) => {
      // A scroll targeted INSIDE the popover (the options list) is the popover
      // scrolling itself — it cannot drift from its button, so it stays open.
      // Any other scroll (page, table wrapper) moves the button under a fixed
      // popover — close it.
      if (popoverRef.current?.contains(event.target as Node)) return;
      close(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  const count = selected.length;
  const toggleOption = (value: string) =>
    onChange(count > 0 && selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);

  return (
    <div className="multiselect" ref={wrapRef}>
      <button
        type="button"
        id={id}
        className={count > 0 ? 'multiselect__toggle multiselect__toggle--active' : 'multiselect__toggle'}
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-label={`${label}: ${count > 0 ? `выбрано ${count}` : placeholder}`}
        title={count > 0 ? `${label}: ${count} выбрано` : label}
        data-testid={testId}
        ref={buttonRef}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <span className="multiselect__name">{count > 0 ? `${label} · ` : placeholder}</span>
        {count > 0 ? <span className="multiselect__count">{count}</span> : null}
        <ChevronDown size={14} aria-hidden="true" className="multiselect__chevron" />
      </button>
      {open ? (
        <div
          id={popoverId}
          className="multiselect__popover"
          role="group"
          aria-label={label}
          data-testid={testId ? `${testId}-popover` : undefined}
          ref={popoverRef}
          // Hidden (off the measurement, never painted) until the layout
          // effect has placed it — no flicker at 0,0.
          style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
        >
          {count > 0 ? (
            <button type="button" className="multiselect__clear" data-testid={testId ? `${testId}-clear` : undefined} onClick={() => onChange([])}>
              <X size={12} aria-hidden="true" /> Очистить
            </button>
          ) : null}
          <ul className="multiselect__list">
            {options.map((option) => {
              const checked = selected.includes(option.value);
              return (
                <li key={option.value}>
                  <label className="multiselect__option">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={checked}
                      data-testid={testId ? `${testId}-opt-${option.value}` : undefined}
                      onChange={() => toggleOption(option.value)}
                    />
                    <span className="multiselect__option-label">{option.label}</span>
                  </label>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
